import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";

export default function GroupBestTwoResults() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      const [scoresRes, groupsRes, holesRes, teamPointsRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/scores/team-points/" + eventRes.data.id).catch(() => ({ data: [] }))
      ]);

      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holesData = holesRes.data;
      const scores = scoresRes.data;
      const isMedal = eventRes.data.game_type?.includes("Medal") || eventRes.data.game_type?.includes("Stroke");
      // Team Stableford (Best 2 of 4) via the kiosk's team-entry screen:
      // points are entered directly per (grp, hole), with no individual
      // scores behind them at all -- use those in place of recomputing
      // from hole_scores whenever present for that group/hole.
      const teamPointsMap = {};
      (teamPointsRes.data || []).forEach(r => { teamPointsMap[r.grp + "_" + r.hole] = r.points; });

      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = [];
        grpMap[p.grp].push(p);
      });

      const groupRows = Object.entries(grpMap)
        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
        .map(([grp, members]) => {
          const names = members.map(p => p.name.split(" ")[0]).join(", ");

          const holeValues = holesData.map(hole => {
            const kioskPoints = teamPointsMap[grp + "_" + hole.hole];
            if (kioskPoints !== undefined) return kioskPoints;
            const memberValues = members.map(p => {
              const s = scores.find(sc => sc.player_id === p.player_id && sc.hole === hole.hole);
              if (!s) return null;
              // 85% of Course Handicap per R&A Rules of Handicapping
              // Appendix C (Best 2 of 4 stroke play) -- fixed 2026-10-25
              // after the AI Scoring Verification Wizard confirmed this
              // format had been running on full (100%) allowance instead,
              // a genuine scoring bug.
              const hcp = Math.round(((p.hcp_adj ?? p.handicap) || 0) * 0.85);
              const base = Math.floor(hcp / 18);
              const extra = hcp % 18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const net = parseInt(s.gross) - shots;
              return isMedal ? net : Math.max(0, 2 + (hole.par - net));
            }).filter(v => v !== null);

            if (memberValues.length === 0) return null;
            memberValues.sort((a, b) => isMedal ? a - b : b - a);
            const best2 = memberValues.slice(0, 2);
            return best2.reduce((sum, v) => sum + v, 0);
          });

          const thru = holeValues.filter(v => v !== null).length;
          const total = holeValues.reduce((sum, v) => sum + (v || 0), 0);

          return { grp: parseInt(grp), names, holeValues, thru, total, wins: {} };
        });

      holesData.forEach((hole, i) => {
        const valid = groupRows.filter(r => r.holeValues[i] !== null);
        if (valid.length === 0) return;
        const best = isMedal
          ? Math.min(...valid.map(r => r.holeValues[i]))
          : Math.max(...valid.map(r => r.holeValues[i]));
        valid.forEach(r => { if (r.holeValues[i] === best) r.wins[i] = true; });
      });

      groupRows.sort((a, b) => {
        if (a.thru === 0 && b.thru === 0) return 0;
        if (a.thru === 0) return 1;
        if (b.thru === 0) return -1;
        return isMedal ? a.total - b.total : b.total - a.total;
      });
      groupRows.forEach((r, i) => { r.pos = i + 1; });

      setHoles(holesData);
      setRows(groupRows);
      window.__wizardResult = groupRows;
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Group Winner Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <span className="text-xs text-gray-500">Best 2 scores per hole, per group ({isMedal ? "lowest net" : "highest points"} counts)</span>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-3 py-2 text-center w-14">Pos</th>
                <th className="px-3 py-2 text-left">Group</th>
                {holes.map(h => <th key={h.hole} className="px-2 py-2 text-center w-10">{h.hole}</th>)}
                <th className="px-3 py-2 text-center w-16">Thru</th>
                <th className="px-3 py-2 text-center w-16">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.grp} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
                  <td className="px-3 py-2 text-center font-bold">{r.pos}</td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">Group {r.grp}</div>
                    <div className="text-xs text-gray-500">{r.names}</div>
                  </td>
                  {r.holeValues.map((v, hi) => (
                    <td key={hi} className={"px-2 py-2 text-center " + (r.wins[hi] ? "bg-yellow-200 font-bold text-green-800" : "text-gray-700")}>
                      {v === null ? "-" : v}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center">{r.thru}</td>
                  <td className="px-3 py-2 text-center font-bold text-blue-700">{r.thru > 0 ? r.total : "-"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={holes.length + 4} className="text-center py-8 text-gray-500">No scores entered yet.</td></tr>
              )}
            </tbody>
          </table>
          {!loading && rows.length === 0 && <div className="text-center py-4 text-gray-400 text-xs">No groups found for the current event.</div>}
        </div>
      </div>
    </Layout>
  );
}
